import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { labelizeLedgerEntry, labelizeSourcePlan } from '@mlm/shared';
import type { LedgerEntry } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Amount } from '../amount';

/**
 * The passbook: ledger entries as statement lines. Each row pairs the
 * humanized kind + its meta (date · plan · level) with a signed `Amount`, so
 * every movement carries hue + sign + word. Shared by the dashboard (recent)
 * and the Earnings page (full ledger).
 */
export function StatementList({
  entries,
  kindLabels,
  planLabels,
  title = 'Statement',
  subtitle,
  viewAllHref,
  footer,
  emptyText = 'No earnings yet. Your commissions will appear here as your team grows.',
}: {
  entries: LedgerEntry[];
  kindLabels?: Record<string, string>;
  planLabels?: Record<string, string>;
  title?: string;
  subtitle?: string;
  viewAllHref?: string;
  /** Optional row rendered inside the card below the list (e.g. pagination). */
  footer?: ReactNode;
  emptyText?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between space-y-0">
        <CardTitle className="flex items-baseline gap-2 text-h3">
          {title}
          {subtitle ? (
            <span className="font-normal text-caption text-muted-foreground">{subtitle}</span>
          ) : null}
        </CardTitle>
        {viewAllHref ? (
          <Link
            to={viewAllHref}
            className="shrink-0 text-caption font-medium text-primary hover:underline"
          >
            View all
          </Link>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {entries.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="divide-y">
            {entries.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 px-6 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium" title={t.kind}>
                    {labelizeLedgerEntry(t.kind, t.metadata, kindLabels)}
                  </div>
                  <div className="mt-0.5 text-caption text-muted-foreground">
                    {fmtDateTime(t.createdAt)} · {labelizeSourcePlan(t.sourcePlan, planLabels)}
                    {t.level != null ? ` · L${t.level}` : ''}
                  </div>
                  {t.orderCode ? (
                    // Order-sourced earning (e.g. dropshipping income): surface the
                    // buyer + Trans# so it doesn't read like an "own order".
                    <div className="mt-0.5 truncate text-caption text-muted-foreground">
                      {t.buyerName ?? 'Customer'} ·{' '}
                      <span className="font-mono">#{t.orderCode}</span>
                    </div>
                  ) : null}
                  {t.fromSlotCode ?? t.triggerSlotCode ? (
                    // The "cause slot" — whose activity triggered this earning.
                    <div className="mt-0.5 truncate text-caption text-muted-foreground">
                      Triggered by{' '}
                      <span className="font-mono">{t.fromSlotCode ?? t.triggerSlotCode}</span>
                    </div>
                  ) : null}
                  {typeof t.metadata?.reason === 'string' && t.metadata.reason.trim() !== '' ? (
                    // Admin-supplied explanation (e.g. a reversal's reason) — the
                    // one line that tells the member WHY this row exists. Wraps
                    // rather than truncates: the explanation is the point.
                    <div className="mt-0.5 text-caption text-muted-foreground">
                      {t.metadata.reason}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {t.currencyKind === 'gc' ? (
                    <Badge variant="outline" className="text-[10px]">
                      GC
                    </Badge>
                  ) : null}
                  <Amount
                    value={t.amount}
                    direction={t.direction}
                    currency={t.currency}
                    className="font-medium"
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {footer ? <div className="border-t px-4 py-3">{footer}</div> : null}
    </Card>
  );
}
